/**
 * Batch Produce page -- configure and trigger video production
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import {
  ArrowLeft,
  Loader2,
  Package,
  CheckCircle2,
  AlertTriangle,
  Video,
  Play,
} from "lucide-react";
import { getEvent, getCreditEstimate, triggerProduction } from "@/lib/api";
import { store, type OutputTarget } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface OutputRow {
  style: OutputTarget["style"];
  count: number;
  prompt: string;
}

// ---------------------------------------------------------------------------
// Production Presets (localStorage)
// ---------------------------------------------------------------------------


export function BatchProduce() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSnapshot(store);

  const [outputs, setOutputs] = useState<OutputRow[]>([
    { style: "tiktok", count: 1, prompt: "" },
  ]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // TODO: preset UI not yet wired up — state + handlers will be added when UI is ready

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["event", id],
    queryFn: () => getEvent(id!),
    enabled: !!id,
  });

  // Pre-populate outputs from event's saved output targets (entered at event creation)
  useEffect(() => {
    if (event?.outputTargets && event.outputTargets.length > 0 && !hasInitialized) {
      setOutputs(
        event.outputTargets.map((t) => ({
          style: t.style,
          count: t.count,
          prompt: "",
        }))
      );
      setHasInitialized(true);
    }
  }, [event, hasInitialized]);

  // Build targets from outputs for credit estimation
  const targets: OutputTarget[] = outputs.map((o) => ({
    style: o.style,
    count: o.count,
  }));

  const {
    data: estimate,
    isLoading: estimateLoading,
    refetch: refetchEstimate,
  } = useQuery({
    queryKey: ["credit-estimate", id, JSON.stringify(targets)],
    queryFn: () => getCreditEstimate(id!, targets),
    enabled: !!id && outputs.length > 0,
  });

  // Refetch estimate when outputs change
  useEffect(() => {
    if (id && outputs.length > 0) {
      refetchEstimate();
    }
  }, [outputs, id, refetchEstimate]);

  const produceMutation = useMutation({
    mutationFn: () => {
      const prompts: Record<number, string> = {};
      outputs.forEach((o, i) => {
        if (o.prompt.trim()) {
          prompts[i] = o.prompt.trim();
        }
      });
      return triggerProduction(
        id!,
        targets,
        Object.keys(prompts).length > 0 ? prompts : undefined
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      navigate(`/events/${id}/outputs`);
    },
    onError: () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  function updateOutput(
    index: number,
    field: keyof OutputRow,
    value: string | number
  ) {
    const updated = [...outputs];
    if (field === "style") {
      updated[index] = {
        ...updated[index],
        style: value as OutputTarget["style"],
      };
    } else if (field === "count") {
      updated[index] = { ...updated[index], count: value as number };
    } else {
      updated[index] = { ...updated[index], prompt: value as string };
    }
    setOutputs(updated);
  }

  const creditBalance = user?.creditBalance ?? 0;
  const totalCredits = estimate?.totalCredits ?? 0;
  const hasEnoughCredits = creditBalance >= totalCredits;

  if (eventLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/events/${id}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-vizimix-100">
              Produce Videos
            </h2>
            <p className="text-sm text-vizimix-400 mt-1">
              {event?.title} -- Configure and start batch production
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/events/${id}/review`)}
            className="gap-1"
          >
            <Video className="h-3 w-3" />
            Review
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/events/${id}/outputs`)}
            className="gap-1"
          >
            <Play className="h-3 w-3" />
            Outputs
          </Button>
        </div>
      </div>

      {/* Approved Clips Info */}
      <Card className="mb-6">
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-400" />
          <span className="text-sm text-vizimix-200">
            {event?.approvedCount ?? 0} approved clips available for production
          </span>
        </CardContent>
      </Card>

      {event?.status !== "closed" && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6 text-sm text-warning-foreground dark:text-warning">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          Production can only be started when the event is closed. Current
          status: {event?.status}
        </div>
      )}

      {produceMutation.error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 text-sm text-destructive">
          {produceMutation.error instanceof Error
            ? produceMutation.error.message
            : "Failed to start production"}
        </div>
      )}

      {/* Output Configuration (locked at event creation) */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Output Configuration</CardTitle>
            <span className="text-xs text-vizimix-400 bg-vizimix-500/10 px-2 py-1 rounded">
              Locked at event creation
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {outputs.map((output, index) => (
            <div
              key={index}
              className="border border-organize-border rounded-lg p-4 space-y-3"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm text-vizimix-400 font-mono w-8">
                  #{index + 1}
                </span>
                <span className="flex h-10 items-center rounded-md border border-organize-border bg-organize-bg/50 px-3 py-2 text-sm text-vizimix-100">
                  {output.style === "tiktok" ? "TikTok" : output.style === "instagram" ? "Instagram" : "YouTube Short"}
                </span>
                <span className="flex h-10 items-center rounded-md border border-organize-border bg-organize-bg/50 px-3 py-2 text-sm text-vizimix-100 w-24 justify-center">
                  ×{output.count}
                </span>
              </div>
              <div>
                <label className="block text-xs text-vizimix-400 mb-1">
                  Custom prompt (optional)
                </label>
                <textarea
                  value={output.prompt}
                  onChange={(e) =>
                    updateOutput(index, "prompt", e.target.value)
                  }
                  placeholder="Describe the style, tone, or specific instructions for this output..."
                  rows={2}
                  className="flex w-full rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Submit */}
      {produceMutation.error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          {produceMutation.error instanceof Error
            ? produceMutation.error.message
            : "Failed to start production"}
        </div>
      )}
      {!hasEnoughCredits && estimate && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-4 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          Insufficient credits. You need {totalCredits - creditBalance} more
          credits ({totalCredits} required, {creditBalance} available).
        </div>
      )}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/events/${id}`)}
        >
          Cancel
        </Button>
        <Button
          disabled={
            !hasEnoughCredits ||
            event?.status !== "closed" ||
            (event?.approvedCount ?? 0) === 0 ||
            produceMutation.isPending ||
            estimateLoading
          }
          onClick={() => setShowConfirm(true)}
          className="gap-2"
        >
          {produceMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting Production...
            </>
          ) : (
            <>
              <Package className="h-4 w-4" />
              Produce Videos
            </>
          )}
        </Button>
      </div>

      {/* Credit Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          produceMutation.mutate();
        }}
        title="Confirm Credit Charge"
        description=""
        confirmLabel="Start Production"
        isLoading={produceMutation.isPending}
      >
        <div className="space-y-3 text-sm">
          <p className="text-vizimix-300">
            Producing {outputs.reduce((sum, o) => sum + o.count, 0)} video(s)
            from {event?.approvedCount ?? 0} approved clips.
          </p>
          {estimate && (
            <div className="space-y-2 bg-organize-bg/50 rounded-lg p-3">
              {estimate.perOutput.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between text-vizimix-300"
                >
                  <span>
                    {item.style} x{outputs[i]?.count ?? 1}
                  </span>
                  <span>{item.credits} credits</span>
                </div>
              ))}
              <div className="border-t border-organize-border pt-2 flex justify-between font-semibold text-vizimix-100">
                <span>Total</span>
                <span>{estimate.totalCredits} credits</span>
              </div>
              <div className="flex justify-between text-vizimix-400">
                <span>Your balance</span>
                <span className="text-success">{creditBalance} credits</span>
              </div>
              <div className="flex justify-between text-vizimix-400">
                <span>After charge</span>
                <span>{creditBalance - totalCredits} credits</span>
              </div>
            </div>
          )}
          <p className="text-xs text-vizimix-500">
            Credits will be reserved immediately. This cannot be undone.
          </p>
        </div>
      </ConfirmDialog>
    </div>
  );
}
